import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { House, Calendar, ListChecks, Users, Sparkles, Clock, CreditCard, BarChart3, LogOut, PlusCircle, FileText, MessageSquare, Settings, MoreHorizontal, X, Megaphone, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Brand";
import { RequestProgress } from '@/components/RequestProgress';

const NAV_ITEMS = [
    { to: "/staff/settings/app", label: "App & notifications", short: "App", icon: Settings, id: "staff-app" },
    { to: "/staff", end: true, label: "Dashboard", short: "Home", icon: House, id: "dashboard" },
    { to: "/staff/calendar", label: "Calendar", short: "Calendar", icon: Calendar, id: "calendar" },
    { to: "/staff/appointments", label: "Appointments", short: "Appts", icon: ListChecks, id: "appointments" },
    { to: "/staff/customers", label: "Customers", short: "Clients", icon: Users, id: "customers" },
    { to: "/staff/services", label: "Services", short: "Services", icon: Sparkles, id: "services" },
    { to: "/staff/forms", label: "Forms", short: "Forms", icon: FileText, id: "forms" },
    { to: "/staff/communications", label: "Communications", short: "Messages", icon: MessageSquare, id: "communications" },
    { to: "/staff/availability", label: "Availability", short: "Hours", icon: Clock, id: "availability" },
    { to: "/staff/payments", label: "Payments", short: "Payments", icon: CreditCard, id: "payments" },
    { to: "/staff/reports", label: "Reports", short: "Reports", icon: BarChart3, id: "reports" },
    { to: "/staff/settings/automations", label: "Automations", short: "Rules", icon: Settings, id: "automations" },
    { to: "/staff/settings/communications", label: "Booking emails", short: "Emails", icon: Mail, id: "comms-settings" },
    { to: "/staff/marketing", label: "Marketing", short: "Marketing", icon: Megaphone, id: "marketing" },
];

// The 4 highest-frequency mobile taps + a "More" opener.
// Every page in NAV_ITEMS remains reachable from mobile via the More sheet.
const MOBILE_PRIMARY_IDS = ["dashboard", "calendar", "appointments", "customers"];
const MOBILE_PRIMARY = MOBILE_PRIMARY_IDS
    .map((id) => NAV_ITEMS.find((n) => n.id === id))
    .filter(Boolean);
const MOBILE_SECONDARY = NAV_ITEMS.filter((n) => !MOBILE_PRIMARY_IDS.includes(n.id));

export function StaffLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const [moreOpen, setMoreOpen] = useState(false);
    // Auto-close the sheet whenever the URL changes so it never lingers.
    React.useEffect(() => { setMoreOpen(false); }, [location.pathname]);
    // Feb-2026: portal switches to a bright, high-contrast light theme.
    // We toggle a body class so we only affect staff routes — the public
    // booking flow (BookingFlow.jsx) keeps its dark luxury aesthetic.
    React.useEffect(() => {
        document.body.classList.add("app-light-mode");
        return () => document.body.classList.remove("app-light-mode");
    }, []);
    const doLogout = async () => { await logout(); nav("/staff/login", { replace: true }); };

    const greeting = (() => {
        const h = new Date().getHours();
        if (h < 12) return "Good morning";
        if (h < 17) return "Good afternoon";
        return "Good evening";
    })();

    return (
        <div className="app-light min-h-[100dvh] flex bg-[#f6f7fb] text-navy relative">
            <RequestProgress />
            {/* Desktop sidenav — NAVY brand rail (matches mockup) */}
            <aside className="hidden md:flex md:flex-col w-64 shrink-0 bg-navy border-r border-navy/50 text-white relative z-10 shadow-[8px_0_24px_-16px_rgba(15,26,61,0.35)]" data-testid="staff-sidenav">
                <div className="px-6 pt-8 pb-6"><Wordmark small /></div>
                <div className="px-6 mb-8">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-gold font-semibold">{greeting}</div>
                    <div className="mt-1 font-serif text-2xl text-white leading-tight font-medium" data-testid="staff-greeting-name">
                        {user?.name || "Angela"}
                    </div>
                </div>
                <nav className="flex-1 px-3 space-y-1">
                    {NAV_ITEMS.map((it) => (
                        <NavLink
                            key={it.id}
                            to={it.to}
                            end={it.end}
                            data-testid={`nav-${it.id}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-full text-sm transition-all ${
                                    isActive
                                        ? "bg-gold/20 text-gold border border-gold/40 font-semibold shadow-[inset_0_0_0_1px_rgba(197,160,89,0.15)]"
                                        : "text-white/75 hover:text-white hover:bg-white/[0.06] border border-transparent"
                                }`
                            }
                        >
                            <it.icon className="h-4 w-4" strokeWidth={1.75} />
                            <span className="font-ui">{it.label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-white/10 pt-4">
                    <NavLink
                        to="/staff/booking/new"
                        data-testid="nav-new-booking"
                        className="w-full h-11 rounded-full bg-gold text-navy font-ui font-semibold text-sm flex items-center justify-center gap-2 shadow-[0_10px_20px_-10px_rgba(197,160,89,0.6)] hover:bg-gold-light transition-all mb-2"
                    >
                        <PlusCircle className="h-4 w-4" strokeWidth={2} />
                        New booking
                    </NavLink>
                    <button
                        onClick={doLogout}
                        data-testid="nav-logout"
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-full text-sm text-white/60 hover:text-white hover:bg-white/[0.06] transition-colors"
                    >
                        <LogOut className="h-4 w-4" strokeWidth={1.75} />
                        <span className="font-ui">Sign out</span>
                    </button>
                </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 min-w-0 flex flex-col relative">
                {/* Mobile top bar — white with the logo + SIGN OUT (matches mockup).
                    Feb-2026 mobile polish: show ONLY on the Home dashboard so every
                    other page reclaims that vertical space. Sign-out remains
                    accessible from the "More" bottom-sheet. */}
                {location.pathname === "/staff" && (
                    <header className="md:hidden sticky top-0 z-30 bg-white border-b hairline px-4 py-2 flex items-center justify-between" style={{ paddingTop: "calc(env(safe-area-inset-top) + 8px)" }}>
                        <Wordmark small />
                        <button onClick={doLogout} className="text-[11px] uppercase tracking-[0.22em] text-navy font-semibold" data-testid="mobile-logout">
                            Sign out
                        </button>
                    </header>
                )}

                <div className={`flex-1 px-4 sm:px-6 md:px-10 py-6 md:py-10 pb-[calc(env(safe-area-inset-bottom)+96px)] md:pb-10 max-w-6xl mx-auto w-full min-w-0 overflow-x-hidden relative ${location.pathname !== "/staff" ? "md:py-10" : ""}`}
                     style={location.pathname !== "/staff" ? { paddingTop: "calc(env(safe-area-inset-top) + 12px)" } : undefined}>
                    <Outlet />
                </div>

                {/* Mobile bottom nav — white surface with navy icons + gold active state */}
                <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t hairline shadow-[0_-8px_24px_-12px_rgba(15,26,61,0.15)]" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="staff-bottomnav">
                    <div className="grid grid-cols-5 max-w-lg mx-auto">
                        {MOBILE_PRIMARY.map((it) => (
                            <NavLink
                                key={it.id}
                                to={it.to}
                                end={it.end}
                                data-testid={`bottom-nav-${it.id}`}
                                className={({ isActive }) =>
                                    `relative flex flex-col items-center justify-center gap-0.5 min-h-11 py-1.5 px-1 text-[9px] uppercase tracking-[0.14em] font-semibold transition-colors ${
                                        isActive ? "text-gold" : "text-navy/60"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        <it.icon className="h-5 w-5" strokeWidth={1.75} />
                                        <span className="w-full text-center leading-none whitespace-nowrap">{it.short || it.label}</span>
                                        {isActive && <span className="absolute bottom-1 h-0.5 w-6 rounded-full bg-gold" />}
                                    </>
                                )}
                            </NavLink>
                        ))}
                        <button type="button" onClick={() => setMoreOpen(true)}
                                data-testid="bottom-nav-more"
                                className={`relative flex flex-col items-center justify-center gap-0.5 min-h-11 py-1.5 px-1 text-[9px] uppercase tracking-[0.14em] font-semibold transition-colors ${
                                    moreOpen ? "text-gold" : "text-navy/60"
                                }`}>
                            <MoreHorizontal className="h-5 w-5" strokeWidth={1.75} />
                            <span className="w-full text-center leading-none whitespace-nowrap">More</span>
                            {moreOpen && <span className="absolute bottom-1 h-0.5 w-6 rounded-full bg-gold" />}
                        </button>
                    </div>
                </nav>

                {/* Mobile "More" slide-up sheet — white sheet with navy icons */}
                {moreOpen && (
                    <div className="md:hidden fixed inset-0 z-40" data-testid="mobile-more-sheet">
                        <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={() => setMoreOpen(false)} />
                        <div className="absolute inset-x-0 bottom-0 bg-white border-t hairline rounded-t-2xl p-4 pb-8 shadow-[0_-16px_40px_-8px_rgba(15,26,61,0.25)]" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)" }}>
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-[10px] uppercase tracking-[0.24em] text-navy/50 font-semibold">All sections</div>
                                <button onClick={() => setMoreOpen(false)} className="text-navy/60" data-testid="mobile-more-close" aria-label="Close">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                {MOBILE_SECONDARY.map((it) => (
                                    <NavLink
                                        key={it.id}
                                        to={it.to}
                                        end={it.end}
                                        onClick={() => setMoreOpen(false)}
                                        data-testid={`more-nav-${it.id}`}
                                        className={({ isActive }) =>
                                            `flex flex-col items-center justify-center gap-1 rounded-xl border py-3 px-2 ${
                                                isActive ? "border-gold/60 bg-gold/10 text-gold" : "border-navy/10 text-navy/80 hover:text-navy hover:bg-navy/[0.04]"
                                            }`
                                        }
                                    >
                                        <it.icon className="h-5 w-5" strokeWidth={1.75} />
                                        <span className="text-[10px] uppercase tracking-[0.14em] text-center leading-tight font-semibold">{it.label}</span>
                                    </NavLink>
                                ))}
                            </div>
                            {/* Sign-out lives here now that the top bar is gone
                                on every non-home page. */}
                            <button
                                type="button"
                                onClick={() => { setMoreOpen(false); doLogout(); }}
                                data-testid="more-sign-out"
                                className="mt-4 w-full min-h-12 h-12 rounded-full border border-navy/15 text-navy font-semibold text-sm inline-flex items-center justify-center gap-2 hover:border-gold/60 hover:text-gold transition-colors"
                            >
                                <LogOut className="h-4 w-4" strokeWidth={1.75} />
                                Sign out
                            </button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
